import { useEffect, useState, useRef, useCallback } from "react";
import {
  Button,
  Label,
  Input,
  Text,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "snap-design-system";
import { useUserData } from "~/store/userStore";
import { ArrowRight, Check, Eye, EyeOff, Info, KeyRound, ShieldAlert, Undo } from "lucide-react";

import { useDialogActions } from "~/store/dialogStore";
import PasswordStrengthMeter, {
  validatePasswordNIST,
} from "~/components/PasswordStrengthMeter";
import { toast } from "sonner";
import { startNewAuthorizedSession } from "~/store/auth/events";
import { useCredentialsStoreActions } from "~/store/credentials";
import { useAuthorization } from "~/hooks/useAuthorization";
import { SK_STORAGE_DURATION } from "~/helpers/constants";
import { arrayBufferToBase64, convertDurationToDate } from "~/helpers";
import { useCredentialStoreEvents } from "~/store/credentials/events";
import { useEncryption } from "~/hooks/useEncryption";
import { MandatoryChangeSecretKeyDialog, type MandatoryChangeFormRef } from "./MandatoryChangeSecretKeyDialog";
import { LegacyMigrationDialog } from "./LegacyMigrationDialog";
import { getLegacyItemsCount } from "~/services/gateways/orgEncryption.gateway";
import { useLegacyMigrationStore } from "~/store/legacyMigrationStore";
import { useQueryClient } from "@tanstack/react-query";

type UserVerificationState = "withVerifier" | "withoutVerifier" | "mustChangeSecretKey";

type PasswordInputProps = {
  isReadOnly?: boolean;
  value: string;
  onChange: (value: string) => void;
};

type StorageDurationSelectorProps = {
  value: string;
  onChange: (value: string) => void;
};

export const PasswordInput = ({ isReadOnly = false, value, onChange }: PasswordInputProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="relative">
      <Input
        id="user-password"
        type={showPassword ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
        placeholder="Digitar senha"
        required
        autoFocus
        readOnly={isReadOnly}
        onFocus={(e) => e.target.select()}
        data-testid="input-secret-key"
      />
      <button
        type="button"
        onClick={togglePasswordVisibility}
        className="absolute right-2 top-1/2 transform -translate-y-1/2 opacity-80 hover:opacity-100 cursor-pointer focus:outline-none"
        aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}
      >
        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
};

export const StorageDurationSelector = ({ value, onChange }: StorageDurationSelectorProps) => {
  return (
    <div className="mt-4">
      <label className="block text-sm font-medium mb-2">
        <div className="flex gap-2 items-center">
          <Tooltip delayDuration={0}>
            <TooltipTrigger className="cursor-help hover:" asChild>
              <Info size={16} />
            </TooltipTrigger>
            <TooltipContent side="right" className="border border-border rounded-md p-2 shadow-2xs">
              <div className="flex gap-1 items-center text-accent-500 py-1">
                <Info size={14} />
                <Text variant="body-sm" className="text-accent-500">Armazenamento temporário</Text>
              </div>
              <Text variant="body-sm">
                Escolha por quanto tempo manter sua senha armazenada para evitar ter que digitar novamente.
              </Text>
            </TooltipContent>
          </Tooltip>
          <Text>Armazenar senha temporariamente por:</Text>
        </div>
      </label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger variant="bottom-solid" className="w-full border-border">
          <SelectValue placeholder="Selecione a duração" />
        </SelectTrigger>
        <SelectContent>
          {SK_STORAGE_DURATION.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export function SecretKeyDialogContent() {
  const [password, setPassword] = useState("");
  const [storageDuration, setStorageDuration] = useState("15m");
  const { updatePassword } = useCredentialsStoreActions();
  const userData = useUserData();

  useEffect(() => {
    updatePassword("");
    (window as any).secretKeyDialogState = { password, setPassword, storageDuration, setStorageDuration };
  }, [password, storageDuration]);

  const getUserState = (
    userData: ReturnType<typeof useUserData>
  ): UserVerificationState => {
    if (userData?.must_change_secret_key) return "mustChangeSecretKey";
    return userData?.verifier ? "withVerifier" : "withoutVerifier";
  };

  const phrases = {
    withVerifier: {
      infoTitle: "Você já possui uma senha associada.",
      infoDescription: (
        <div className="space-y-1">
          <Text variant="body-sm" className="">
            Sua senha não fica salva em nosso sistema por segurança.
          </Text>
          <Text variant="body-sm" className="">
            Digite-a novamente para acessar seus dados criptografados.
          </Text>
        </div>
      ),
      inputLabel: "Favor inserir a senha para visualizar os dados:",
    },
    withoutVerifier: {
      infoTitle: "Necessário criar uma senha para associar a sua conta.",
      infoDescription: (
        <div className="space-y-1">
          <Text variant="body-sm" className="">
            Crie uma senha e guarde-a em um lugar seguro.
          </Text>
          <Text variant="body-sm" className="">
            Ela não será salva em nosso sistema e não será possível recuperá-la.
          </Text>
          <Text variant="body-sm" className="">
            Todos os dados dos relatórios serão criptografados com esta senha.
          </Text>
          <Text variant="body-sm" className="">
            Se perdê-la, não será possível descriptografar os dados.
          </Text>
        </div>
      ),
      inputLabel: "Favor inserir uma senha para associar a sua conta:",
    },
    mustChangeSecretKey: {
      infoTitle: "Sua senha de criptografia pessoal foi redefinida por um administrador.",
      infoDescription: (
        <div className="space-y-1">
          <Text variant="body-sm">
            Um administrador redefiniu sua senha de criptografia pessoal.
          </Text>
          <Text variant="body-sm">
            Digite a senha temporária informada pelo administrador para prosseguir.
          </Text>
          <Text variant="body-sm">
            Em seguida, você deverá criar uma nova senha de criptografia pessoal.
          </Text>
        </div>
      ),
      inputLabel: "Insira a senha temporária de criptografia fornecida pelo administrador:",
    },
  };

  const getPhrase = (key: keyof typeof phrases.withVerifier): string | React.ReactElement => {
    const userState = getUserState(userData);
    return phrases[userState][key];
  };

  return (
    <div className="">
      <form
        id="secret-key-form"
        onSubmit={(e) => {
          e.preventDefault();
          document.dispatchEvent(new Event("submit-secret-key"));
        }}
      >
        <label htmlFor="user-password" className="flex gap-2 items-center">
          <Tooltip delayDuration={0}>
            <TooltipTrigger className="cursor-help" asChild>
              <Info size={16} />
            </TooltipTrigger>
            <TooltipContent side="right">
              <div className="flex gap-1 items-center text-accent-500 py-1">
                <Info size={14} />
                <Text variant={"body-sm"} className="text-accent-500">{getPhrase("infoTitle")}</Text>
              </div>
              <Text variant="body-sm">
                {getPhrase("infoDescription")}
              </Text>
            </TooltipContent>
          </Tooltip>
          <Text>{getPhrase("inputLabel")}</Text>
        </label>
        <PasswordInput value={password} onChange={(value) => {
          setPassword(value);
          if ((window as any).secretKeyDialogState?.setPassword) {
            (window as any).secretKeyDialogState.setPassword(value);
          }
        }} />
        {!userData?.verifier && !userData?.must_change_secret_key && (
          <PasswordStrengthMeter password={password} />
        )}

        {!userData?.must_change_secret_key && (
          <StorageDurationSelector
            value={storageDuration}
            onChange={(value) => {
              setStorageDuration(value);
              if ((window as any).secretKeyDialogState?.setStorageDuration) {
                (window as any).secretKeyDialogState.setStorageDuration(value);
              }
            }}
          />
        )}
      </form>
    </div>
  );
}

export function SecretKeyDialogFooter({
  onOpen: onOpenUserSecretKeyDialog,
  onSuccess,
}: { onOpen: () => void; onSuccess?: () => void }) {
  const { persistPassword, updatePassword } = useCredentialsStoreActions();
  const { handleVerifier } = useAuthorization();
  const { closeDialog, openDialog } = useDialogActions();
  const { useHasValidVerifiers } = useCredentialStoreEvents();
  const hasValidVerifiers = useHasValidVerifiers();
  const userData = useUserData();
  const { deriveKey } = useEncryption();
  const queryClient = useQueryClient();

  // Stable ref shared between Content and Footer — avoids stale closure in openDialog snapshot
  const mandatoryFormRef = useRef<MandatoryChangeFormRef>({
    newPassword: "",
    confirmPassword: "",
    duration: "15m",
  });

  // Ref para progresso da migração legada
  const migrationRef = useRef<{ isComplete: boolean; hasFailed: boolean }>({
    isComplete: false,
    hasFailed: false,
  });

  /**
   * Verifica se existem itens legados e abre dialog de migração se necessário.
   * Retorna true se a migração foi iniciada (dialog aberto), false caso contrário.
   */
  const checkAndStartLegacyMigration = useCallback(async (finalCallback?: () => void) => {
    try {
      const counts = await getLegacyItemsCount();
      if (counts.total > 0) {
        migrationRef.current = { isComplete: false, hasFailed: false };
        // Block report list queries while migration is running
        useLegacyMigrationStore.getState().setMigrationPending(true);

        openDialog({
          title: "Atualização de segurança",
          icon: <ShieldAlert />,
          content: (
            <LegacyMigrationDialog.Content
              totalItems={counts.total}
              migrationRef={migrationRef}
            />
          ),
          footer: (
            <LegacyMigrationDialog.Footer
              migrationRef={migrationRef}
              onSuccess={() => {
                // Unblock report list queries and trigger refetch
                useLegacyMigrationStore.getState().setMigrationPending(false);
                // Force fresh fetch — cached data has stale envelope fields
                queryClient.invalidateQueries({ queryKey: ["reports", "list"] });
                closeDialog();
                finalCallback?.();
              }}
            />
          ),
          hideCloseButton: true,
          preventOutsideClose: true,
          className: "max-w-lg",
        });
        return true;
      }
    } catch (err) {
      // Se falhar ao verificar, não bloqueia o fluxo
      useLegacyMigrationStore.getState().setMigrationPending(false);
      console.warn("[SecretKeyDialog] Failed to check legacy items:", err);
    }
    return false;
  }, [openDialog, closeDialog, queryClient]);

  /** Finaliza o fluxo de autenticação: persiste chave, inicia sessão */
  const finishAuth = useCallback(() => {
    startNewAuthorizedSession(userData?.roles || []);
    closeDialog();
    toast.success("Senha validada com sucesso!");
    onSuccess?.();
  }, [userData, closeDialog, onSuccess]);

  const getSharedState = () => (window as any).secretKeyDialogState || { password: "", storageDuration: "15m" };

  const checkPassword = () => {
    const { password } = getSharedState();
    const trimmedPassword = password?.trim();
    const validation = validatePasswordNIST(trimmedPassword || "");
    if (!validation.isValid) {
      toast.error("Senha insegura", {
        description: validation.errorMessage,
      });
      return false;
    }
    return true;
  };

  const handleOpenSaveDialog = () => {
    if (!checkPassword()) return;

    openDialog({
      title: "confirmar senha".toUpperCase(),
      icon: <KeyRound />,
      content: (
        <form
          id="confirm-password-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="flex flex-col">
            <Text variant="body-md">
              Sua senha não ficará armazenada em nosso sistema.
            </Text>
            <Text variant="body-md" className="pb-2">
              Guarde-a em um lugar seguro antes de prosseguir.
            </Text>
            <Label>Senha confirmada:</Label>
            <PasswordInput isReadOnly value={getSharedState().password} onChange={() => { }} />
          </div>
        </form>
      ),
      footer: (
        <>
          <Button variant="ghost" onClick={onOpenUserSecretKeyDialog} icon={<Undo />} iconPosition="right">
            Alterar
          </Button>
          <Button
            type="submit"
            form="confirm-password-form"
            icon={<Check size={16} />}
            iconPosition="right"
          >
            Confirmar
          </Button>
        </>
      ),
      className: "max-w-fit"
    });
  };

  const handleSubmit = async () => {
    const { password, storageDuration } = getSharedState();
    const trimmedPassword = password?.trim();
    if (!trimmedPassword) {
      toast.error("Erro ao validar senha.", { description: "A senha não pode estar vazia." });
      return;
    }

    try {
      const ok = await handleVerifier(trimmedPassword);
      if (ok) {
        if (userData?.must_change_secret_key) {
          // Reset the ref for a fresh dialog
          mandatoryFormRef.current = { newPassword: "", confirmPassword: "", duration: "15m" };
          openDialog({
            title: "Alteração de senha obrigatória",
            icon: <ShieldAlert />,
            content: (
              <MandatoryChangeSecretKeyDialog.Content
                formRef={mandatoryFormRef}
              />
            ),
            footer: (
              <MandatoryChangeSecretKeyDialog.Footer
                currentTempPassword={trimmedPassword}
                formRef={mandatoryFormRef}
                onSuccess={async () => {
                  // Após troca obrigatória, verifica itens legados.
                  // Nota: MandatoryChangeSecretKeyFooter já chamou startNewAuthorizedSession
                  // e closeDialog, então aqui só precisamos verificar migração legada.
                  const migrationStarted = await checkAndStartLegacyMigration(() => {
                    onSuccess?.();
                  });
                  if (!migrationStarted) {
                    onSuccess?.();
                  }
                }}
              />
            ),
            className: "max-w-lg",
          });
          return;
        }

        const rawKey = await deriveKey(trimmedPassword);
        const keyBytes = rawKey.buffer.slice(rawKey.byteOffset, rawKey.byteOffset + rawKey.byteLength);
        const storedKey = arrayBufferToBase64(keyBytes as ArrayBuffer);
        updatePassword(storedKey);
        const expirationDate = convertDurationToDate(storageDuration);
        if (expirationDate) {
          persistPassword(expirationDate);
        }

        // Verifica itens legados antes de finalizar
        const migrationStarted = await checkAndStartLegacyMigration(finishAuth);
        if (!migrationStarted) {
          finishAuth();
        }
      }
    } catch (error) {
      toast.error("Erro ao validar senha.", { description: "A senha informada não é válida." });
    }
  };

  useEffect(() => {
    const handleFormSubmit = () => {
      if (!hasValidVerifiers) {
        handleOpenSaveDialog();
      } else {
        handleSubmit();
      }
    };
    document.addEventListener("submit-secret-key", handleFormSubmit);
    return () => document.removeEventListener("submit-secret-key", handleFormSubmit);
  }, [onSuccess, hasValidVerifiers]);

  return (
    <div className="flex justify-end w-full">
      <Button
        onClick={!hasValidVerifiers ? handleOpenSaveDialog : handleSubmit}
        data-testid="button-confirm-secret-key"
        icon={!hasValidVerifiers ? <ArrowRight /> : <Check />}
        iconPosition="right"
        variant="primary"
      >
        {!hasValidVerifiers ? "Prosseguir" : "Confirmar"}
      </Button>
    </div>
  );
}

export const SecretKeyDialog = {
  Content: SecretKeyDialogContent,
  Footer: SecretKeyDialogFooter,
};